import { z } from 'zod'
import {
  systemEventCellIdSchema,
  systemEventContainerSchema,
  systemEventEnvelopeSchema,
  type SystemEventEntityType,
  type SystemEventStatus,
} from './primitives.js'

type StrictObject<TShape extends z.ZodRawShape> = ReturnType<typeof z.strictObject<TShape>>

/** Capture owns this metadata; operation descriptions continue to validate only their own data. */
const capturedContextShape = { container: systemEventContainerSchema.nullable().optional() }

type CapturedDataSchema<TData extends z.ZodObject> = z.ZodObject<
  z.core.util.Extend<TData['shape'], typeof capturedContextShape>,
  TData['_zod']['config']
>
type EntitySchema<TEntity extends SystemEventEntityType | null> = StrictObject<{
  type: z.ZodLiteral<TEntity>
  id: z.ZodNullable<z.ZodString>
}>
type DescriptionShape<
  TType extends string,
  TStatus extends SystemEventStatus,
  TEntity extends SystemEventEntityType | null,
  TData extends z.ZodObject,
> = {
  eventType: z.ZodLiteral<TType>
  eventStatus: z.ZodLiteral<TStatus>
  key: z.ZodString
  entity: EntitySchema<TEntity>
  data: TData
}
/** What a caller supplies. The key defaults to the entity ID, then the event type. */
type DescriptionInput<
  TEntity extends SystemEventEntityType | null,
  TData extends z.ZodObject,
> = StrictObject<{
  key: z.ZodOptional<z.ZodString>
  entity: EntitySchema<TEntity>
  data: TData
}>

export type SystemEventDefinition<
  TType extends string,
  TStatus extends SystemEventStatus,
  TEntity extends SystemEventEntityType | null,
  TData extends z.ZodObject,
> = {
  type: TType
  status: TStatus
  dataSchema: CapturedDataSchema<TData>
  descriptionSchema: StrictObject<DescriptionShape<TType, TStatus, TEntity, TData>>
  eventSchema: StrictObject<
    typeof systemEventEnvelopeSchema.shape & {
      eventType: z.ZodLiteral<TType>
      eventStatus: z.ZodLiteral<TStatus>
      entityType: z.ZodLiteral<TEntity>
      entityId: z.ZodNullable<z.ZodString>
      data: CapturedDataSchema<TData>
    }
  >
  describe: (
    input: z.input<DescriptionInput<TEntity, TData>>,
  ) => z.output<StrictObject<DescriptionShape<TType, TStatus, TEntity, TData>>>
}

const uuidSchema = z.uuid()

const isEntityId = (type: SystemEventEntityType | null, id: string | null): boolean => {
  if (id === null) {
    return true
  }
  if (type === 'TABLE_CELL') {
    return systemEventCellIdSchema.safeParse(id).success
  }
  return type !== null && type !== 'WEBHOOK' && uuidSchema.safeParse(id).success
}

/**
 * Defines the payload and allowed targets for one event type/status pair.
 * A failed creation can target its existing container, or use a null ID when no instance exists.
 * An event without an entity uses [null].
 * WEBHOOK identifies a receipt, not a persisted Doss object, so its entity ID is always null.
 * Entity IDs are UUIDs, except a TABLE_CELL's synthetic `<rowId>:<columnId>`.
 * A description's key defaults to its entity ID, then its event type. Supply one when several
 * descriptions in one capture share an entity, e.g. failures against the same container, and keep
 * an explicit key wherever a caller looks the captured reference up by it afterwards, as the v1
 * telemetry publisher, the v2 interpreter and the webhook intake router do for linking.
 */
export const defineSystemEvent = <
  const TType extends string,
  const TStatus extends SystemEventStatus,
  const TEntities extends readonly [
    SystemEventEntityType | null,
    ...(SystemEventEntityType | null)[],
  ],
  TData extends z.ZodObject,
>({
  type,
  status,
  entityTypes,
  data,
}: {
  type: TType
  status: TStatus
  entityTypes: TEntities
  data: TData
}): SystemEventDefinition<TType, TStatus, TEntities[number], TData> => {
  const entityTypeSchema = z.literal(entityTypes)
  // Zod infers extend against TData's constraint; retain the concrete payload shape and configuration.
  const capturedData = data.extend(capturedContextShape) as CapturedDataSchema<TData>
  const entitySchema = z
    .strictObject({ type: entityTypeSchema, id: z.string().nullable() })
    .refine((entity) => isEntityId(entity.type, entity.id), {
      message: 'Invalid entity ID for this entity type',
      path: ['id'],
    })
  const inputSchema = z.strictObject({
    key: z.string().min(1).optional(),
    entity: entitySchema,
    data,
  })
  const descriptionSchema: SystemEventDefinition<
    TType,
    TStatus,
    TEntities[number],
    TData
  >['descriptionSchema'] = inputSchema.extend({
    key: z.string().min(1),
    eventType: z.literal(type),
    eventStatus: z.literal(status),
  })
  const eventSchema = z
    .strictObject({
      ...systemEventEnvelopeSchema.shape,
      eventType: z.literal(type),
      eventStatus: z.literal(status),
      entityType: entityTypeSchema,
      entityId: z.string().nullable(),
      data: capturedData,
    })
    .refine((event) => isEntityId(event.entityType, event.entityId), {
      message: 'Invalid entity ID for this entity type',
      path: ['entityId'],
    })

  return {
    type,
    status,
    dataSchema: capturedData,
    descriptionSchema,
    eventSchema,
    // Validation happens when capture calls the builder, inside the wrapper's error boundary.
    describe: (input): z.output<typeof descriptionSchema> =>
      descriptionSchema.parse({
        ...input,
        key: input.key ?? input.entity.id ?? type,
        eventType: type,
        eventStatus: status,
      }),
  }
}
